import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Forms, Loading, Modal, SaveButton, SystemText, TitleBar } from '@salesperformanceint/e4shared';

import { adminLoading, update } from 'actions/admin';
import { addImage, setPrismMenu, setShowSuccess } from 'actions/ui';
import PageHeader from 'components/general/pageHeader';
import { apiGet, apiPatch } from 'services';
import { hasModule } from 'helpers/profile';
import config from '../../../config';
import { updateUserCompanySettings } from 'actions/user';
import { e4Format, e4Formats } from 'helpers/dates';
import { AvailableHomeWidgets } from 'views/home';
import { Slider } from '@material-ui/core';

export const AdminSettings = () => {
    const dispatch = useDispatch();

    const api = {
        benchmark: () =>
            apiPatch(`benchmark`, null, user.guid, () => {
                setBenchmarking(false);
                api.load();
            }),
        save: (settings, callback) =>
            dispatch(
                update(
                    'setting',
                    settings,
                    ['logo', 'defaultBlogImage'],
                    () => {
                        dispatch(adminLoading(false));
                        callback && callback();
                    },
                    false
                )
            ),
        load: () =>
            apiGet(
                `setting`,
                user.guid,
                response => {
                    setRawSettings(response);
                    const parsed = JSON.parse(response);
                    setCompany(parsed);
                    parsed.settings && setSettings(JSON.parse(parsed.settings));
                    setLoading(false);
                },
                null,
                true
            ),
    };

    // General
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [rawSettings, setRawSettings] = React.useState('{}');
    const [company, setCompany] = React.useState(null);
    const [settings, setSettings] = React.useState(null);

    // Contact Details
    const [contact, setContact] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [phone, setPhone] = React.useState('');

    // Custom Text
    const [ratingsExplaination, setRatingsExplaination] = React.useState('');
    const [reviewIntroUser, setReviewIntroUser] = React.useState('');
    const [reviewIntroManager, setReviewIntroManager] = React.useState('');

    // Display Settings
    const [logo, setLogo] = React.useState('');
    const [logoMeta, setLogoMeta] = React.useState(null);
    const [onWhite, setOnWhite] = React.useState(false);
    const [homeWidgets, setHomeWidgets] = React.useState([]);
    const [hideHomePage, setHideHomePage] = React.useState([]);
    const [hideMeetingsOnModel, setHideMeetingsOnModel] = React.useState(false);
    const [currencySymbol, setCurrencySymbol] = React.useState('£');
    const [ratingRange, setRatingRange] = React.useState(5);
    const [fy_Start_Date, setFy_Start_Date] = React.useState(null);
    const [defaultBlogImage, setDefaultBlogImage] = React.useState('');
    const [defaultBlogImageMeta, setDefaultBlogImageMeta] = React.useState(null);
    const [showEmailWarning, setShowEmailWarning] = React.useState(false);

    // Metric Ordering & Scope
    const [meetingDays, setMeetingDays] = React.useState(7);

    // Modules
    const [modules, setModules] = React.useState(null);

    // Notifications
    const [notifications, setNotifications] = React.useState([]);

    // Onboardng
    const [introEmail, setIntroEmail] = React.useState('');
    const [due, setDue] = React.useState(30);

    // Development Options
    const [includeObjectivesInReviewPDF, setIncludeObjectivesInReviewPDF] = React.useState(false);
    const [includePositiveIndicatorsInReviewPDF, setIncludePositiveIndicatorsInReviewPDF] = React.useState(false);
    const [usePerformanceDashboard, setUsePerformanceDashboard] = React.useState(true);
    const [showNotesOnPerformanceDashboard, setShowNotesOnPerformanceDashboard] = React.useState(false);
    const [allowDeletionOfPastReviews, setAllowDeletionOfPastReviews] = React.useState(false);
    const [showRecommends, setShowRecommends] = React.useState(false);
    const [usePositiveIndicators, setUsePositiveIndicators] = React.useState(false);
    const [allowCoaches, setAllowCoaches] = React.useState(false);
    const [coachPermissions, setCoachPermissions] = React.useState([]);
    const [expandRatingDescriptions, setExpandRatingDescriptions] = React.useState(false);
    const [greyRatingsForUserReviews, setGreyRatingsForUserReviews] = React.useState(false);
    const [reviewWeighting, setReviewWeighting] = React.useState(50);

    // Benchmarking
    const [benchmarking, setBenchmarking] = React.useState(false);

    // Redux
    const user = useSelector(state => state.user.user);
    const { prism_UI } = user.companySettings;
    const prismSuffix = prism_UI ? '/Prism' : '';

    const competencies = useSelector(state => state.user.downlineCompetencies);
    const { tags, handles, images } = useSelector(state => state.ui);

    const homeWidgetData = [
        { id: 'whatsHappening', name: SystemText('Home/Widgets/WhatsHappening/Title'), disabled: true },
        { id: 'team', name: SystemText('Home/Widgets/Team/Title'), disabled: true },
        { id: 'coach', name: SystemText('Home/Widgets/Coach/Title') },
        { id: 'badges', name: SystemText('Home/Widgets/Badges/Title') },
        { id: 'leaderboard', name: SystemText('Home/Widgets/Leaderboard/Title') },
    ];

    hasModule(user, 'integrations') && homeWidgetData.push({ id: 'goals', name: SystemText('Home/Widgets/Goals/Title') });

    hasModule(user, 'blogs') && homeWidgetData.push({ id: 'blogs', name: SystemText('Home/Widgets/Posts/Title') });

    hasModule(user, 'knowledge') &&
        homeWidgetData.push({ id: 'knowledge', name: SystemText('Home/Widgets/Knowledge/Title') });

    React.useEffect(() => {
        dispatch(setPrismMenu('Admin'));
    }, []);

    React.useEffect(() => {
        api.load();
    }, []);

    React.useEffect(() => {
        if (settings) {
            // Contact Details
            setContact(company.contact);
            setEmail(company.email);
            setPhone(company.phone);

            // Custom Text
            setRatingsExplaination(settings.customText.ratingsExplaination || SystemText('Admin/Config/Defaults/Ratings'));
            setReviewIntroUser(
                settings.customText.reviewIntroUser || SystemText('Reviews/Appraisals/Wizard/Paragraphs/Intro/You')
            );
            setReviewIntroManager(
                settings.customText.reviewIntroManager || SystemText('Reviews/Appraisals/Wizard/Paragraphs/Intro/Them')
            );

            // Display Settings
            setLogo(settings.logo);
            setOnWhite(settings.onWhite);
            setCurrencySymbol(settings.currencySymbol);
            setRatingRange(settings.display.ratingRange || 5);
            setFy_Start_Date(company.fy_Start_Date);
            setHomeWidgets(settings.display.homeWidgets || AvailableHomeWidgets);
            setHideHomePage(settings.display.hideHomePage);
            setHideMeetingsOnModel(settings.display.hideMeetingsOnModel);
            setDefaultBlogImage(settings.display.defaultBlogImage);

            // Metric Ordering & Scope
            setMeetingDays(settings.metrics?.meetingDays);

            // Modules
            setModules(company.modules || []);

            // Notifications
            setNotifications(settings.notifications || []);

            // Onboardng
            const prismSuffix = user.companySettings?.prism_UI ? '/Prism' : '';
            setIntroEmail(settings.onboarding.introEmail || SystemText(`Admin/Config/Defaults/Invite${prismSuffix}`));
            setDue(settings.onboarding.due);

            // Development Options
            setIncludeObjectivesInReviewPDF(settings.development?.includeObjectivesInReviewPDF);
            setIncludePositiveIndicatorsInReviewPDF(settings.development?.includePositiveIndicatorsInReviewPDF);
            setUsePerformanceDashboard(settings.development?.usePerformanceDashboard);
            setShowNotesOnPerformanceDashboard(settings.development?.showNotesOnPerformanceDashboard);
            setAllowDeletionOfPastReviews(settings.development?.allowDeletionOfPastReviews);
            setShowRecommends(settings.development?.showRecommends);
            setUsePositiveIndicators(settings.development?.usePositiveIndicators);
            setAllowCoaches(settings.development?.allowCoaches);
            setCoachPermissions(settings.development?.coachPermissions || []);
            setExpandRatingDescriptions(settings.development?.expandRatingDescriptions);
            setGreyRatingsForUserReviews(settings.development?.greyRatingsForUserReviews);
            setReviewWeighting(settings.development?.reviewWeighting);
        }
    }, [settings]);

    const benchmark = () => {
        setBenchmarking(true);
        api.benchmark();
    };

    const save = () => {
        const parsedJson = JSON.parse(rawSettings);

        const updatedSettings = {
            ...parsedJson,
            integration: null,
            logoMeta,
            notifications,
            modules,
            onWhite,
            currencySymbol,
            display: {
                ratingRange,
                homeWidgets,
                hideHomePage,
                hideMeetingsOnModel,
                defaultBlogImageMeta,
            },
            customText: {
                ratingsExplaination,
                reviewIntroUser,
                reviewIntroManager,
            },
            metrics: {
                meetingDays:
                    !meetingDays || isNaN(meetingDays) || isNaN(parseInt(meetingDays, 10)) || parseInt(meetingDays, 10) <= 0
                        ? 7
                        : parseInt(meetingDays, 10),
            },
            onboarding: {
                introEmail,
                due: isNaN(due) ? 30 : parseInt(due, 10),
            },
            development: {
                includeObjectivesInReviewPDF,
                includePositiveIndicatorsInReviewPDF,
                usePerformanceDashboard,
                showNotesOnPerformanceDashboard,
                allowDeletionOfPastReviews,
                showRecommends,
                usePositiveIndicators,
                allowCoaches,
                coachPermissions,
                expandRatingDescriptions,
                greyRatingsForUserReviews,
                reviewWeighting,
            },
        };

        const settings = JSON.stringify(updatedSettings);

        if (logo && typeof logo === 'object') {
            logo.companyId = user.company_Id;
        }

        if (defaultBlogImage && typeof defaultBlogImage === 'object') {
            defaultBlogImage.companyId = user.company_Id;
        }

        const result = {
            ...company,
            contact,
            email,
            phone,
            fy_Start_Date: fy_Start_Date || new Date(2020, 1, 1),
            modules,
            notifications,
            logo,
            defaultBlogImage,
            settings,
        };

        api.save(result, () => {
            dispatch(updateUserCompanySettings(updatedSettings));
            dispatch(setShowSuccess(st('Success')));
            setSaving(false);

            const json = updatedSettings.customText ? JSON.stringify(updatedSettings.customText) : '';
            sessionStorage.setItem('customText', json);
        });
    };

    const canEditModules = user.companySettings?.pro || user.support;

    const areasMapped = competencies
        ? competencies.reduce(
              (all, c) =>
                  all.filter(a => a.id === c.area_Id).length > 0 ? all : all.concat({ id: c.area_Id, name: c.area }),
              []
          )
        : [];

    areasMapped.sort((a, b) => (a.name > b.name ? 1 : -1));

    const st = (path, ...rest) => SystemText(`Admin/Config/${path}`, ...rest);

    const availableCoachPermissions = [
        { id: '121/View', name: st('Fields/CoachPermissions/121/View') },
        { id: 'Objectives/View', name: st('Fields/CoachPermissions/Objectives/View') },
        { id: 'Pathways/View', name: st('Fields/CoachPermissions/Pathways/View') },
        { id: 'Reviews/View', name: st('Fields/CoachPermissions/Reviews/View') },
        { id: '121/Manage', name: st('Fields/CoachPermissions/121/Manage') },
        { id: 'Objectives/Manage', name: st('Fields/CoachPermissions/Objectives/Manage') },
        { id: 'Pathways/Manage', name: st('Fields/CoachPermissions/Pathways/Manage') },
        { id: 'Reviews/Manage', name: st('Fields/CoachPermissions/Reviews/Manage') },
    ];

    const runSetCoachPermissions = temp => {
        if (temp.some(p => p === '121/Manage')) temp.push('121/View');
        if (temp.some(p => p === 'Objectives/Manage')) temp.push('Objectives/View');
        if (temp.some(p => p === 'Pathways/Manage')) temp.push('Pathways/View');
        if (temp.some(p => p === 'Reviews/Manage')) temp.push('Reviews/View');
        setCoachPermissions([...new Set(temp)]);
    };

    const disabledCoachPermissions = () => {
        const temp = [...availableCoachPermissions];
        if (coachPermissions.some(p => p === '121/Manage')) temp.find(x => x.id === '121/View').disabled = true;
        if (coachPermissions.some(p => p === 'Objectives/Manage'))
            temp.find(x => x.id === 'Objectives/View').disabled = true;
        if (coachPermissions.some(p => p === 'Pathways/Manage')) temp.find(x => x.id === 'Pathways/View').disabled = true;
        if (coachPermissions.some(p => p === 'Reviews/Manage')) temp.find(x => x.id === 'Reviews/View').disabled = true;
        return temp;
    };

    const coachPermissionsHelp = (
        <div>
            {availableCoachPermissions.map((x, idx) => (
                <div className="mb5" key={`coachperm-${idx}`}>
                    <b>{x.name}</b>
                    <div>{st(`Fields/CoachPermissions/${x.id}/Help`)}</div>
                </div>
            ))}
        </div>
    );

    const setModulesValue = (module, value) => {
        let tempModules = (modules || []).filter(m => m !== module);
        if (value) tempModules = tempModules.concat([module]);
        setModules(tempModules);
    };

    const setNotificationValue = (type, value) => {
        let temp = notifications.filter(m => m !== type);
        if (value) temp = temp.concat([type]);
        setNotifications(temp);
    };

    const checkDisable = disable => {
        if (!disable) {
            setNotificationValue('all', true);
        } else {
            setNotificationValue('all', false);
        }

        setShowEmailWarning(false);
    };

    return (
        <div>
            <PageHeader title="Configuration" adminMenu />
            <section className="content mtm10">
                {loading ? (
                    <Loading />
                ) : (
                    <div className="row">
                        <div className="col-md-12">
                            <Forms.Label label={st('Fields/Registered/Label')} value={company.name} />
                            <Forms.Label
                                label={st('Fields/Licences/Label')}
                                value={
                                    company.zero_Licences > 0
                                        ? `${st('Fields/Licences/Full')}: ${company.licences} / ${st(
                                              'Fields/Licences/Zero'
                                          )}: ${company.zero_Licences}`
                                        : company.licences === 1
                                          ? st('Fields/Licences/Trial')
                                          : company.licences
                                }
                            />
                            <Forms.Label
                                label={st('Fields/Licences/Used')}
                                value={
                                    company.zero_Licences > 0
                                        ? `${st('Fields/Licences/Full')}: ${company.full_Used} / ${st(
                                              'Fields/Licences/Zero'
                                          )}: ${company.zero_Used}`
                                        : company.full_Used
                                }
                            />
                            <div id="permission" className="form-group pb10">
                                <label htmlFor="item-name">{st('Fields/Benchmarking/Label')}</label>
                                <div className="flex flex-center mb10">
                                    <SaveButton
                                        data-testid="button-benchmarking"
                                        saving={benchmarking}
                                        onClick={benchmark}
                                        disabled={company.benchmark_Date}
                                        className="btn btn-sm btn-success"
                                    >
                                        {st('Fields/Benchmarking/Button')}
                                    </SaveButton>
                                    {company.benchmark_Date && (
                                        <div className="flex-grow ml5">
                                            <i className="text-success fa fa-check-circle mr5" />
                                            <span className="text-sm">
                                                {st(
                                                    'Fields/Benchmarking/AgreedOn',
                                                    company.benchmark_Full_Name,
                                                    e4Format(company.benchmark_Date, e4Formats.long)
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <TitleBar title={st('Fields/ContactDetails/Label')} data-testid="bar-contact">
                                <Forms.Text
                                    id="contact"
                                    key="contact"
                                    label={st('Fields/Contact/Label')}
                                    onChange={value => setContact(value)}
                                    value={contact}
                                    data-testid="text-contact"
                                />
                                <Forms.Text
                                    id="email"
                                    key="email"
                                    label={st('Fields/Email/Label')}
                                    onChange={value => setEmail(value)}
                                    value={email}
                                    data-testid="text-email"
                                />
                                <Forms.Text
                                    id="phone"
                                    key="phone"
                                    label={st('Fields/Phone/Label')}
                                    onChange={value => setPhone(value)}
                                    value={phone}
                                    data-testid="text-phone"
                                />
                            </TitleBar>
                            <TitleBar title={st('Fields/CustomText/Label')} data-testid="bar-text">
                                <Forms.Textarea
                                    wysiwyg
                                    tags={tags}
                                    handles={handles}
                                    label={st('Fields/RatingsExplanation/Label')}
                                    help={st('Fields/RatingsExplanation/Help')}
                                    rows="10"
                                    onChange={value => setRatingsExplaination(value)}
                                    value={ratingsExplaination}
                                    data-testid="text-ratings-explaination"
                                />
                                <Forms.Textarea
                                    wysiwyg
                                    tags={tags}
                                    handles={handles}
                                    label={st('Fields/ReviewIntroUser/Label')}
                                    help={st('Fields/ReviewIntroUser/Help')}
                                    rows="10"
                                    onChange={value => setReviewIntroUser(value)}
                                    value={reviewIntroUser}
                                    data-testid="text-review-intro-user"
                                />
                                <Forms.Textarea
                                    wysiwyg
                                    tags={tags}
                                    handles={handles}
                                    label={st('Fields/ReviewIntroManager/Label')}
                                    help={st('Fields/ReviewIntroManager/Help')}
                                    rows="10"
                                    onChange={value => setReviewIntroManager(value)}
                                    value={reviewIntroManager}
                                    data-testid="text-review-intro-manager"
                                />
                            </TitleBar>
                            <TitleBar title={st('Fields/DevelopmentOptions/Label')} data-testid="bar-development">
                                <Forms.Toggle
                                    label={st('Fields/AllowCoaches/Label')}
                                    help={st('Fields/AllowCoaches/Help')}
                                    onChange={value => setAllowCoaches(value)}
                                    value={allowCoaches}
                                    data-testid="toggle-allow-coaches"
                                />
                                {allowCoaches && (
                                    <Forms.Select
                                        label={st('Fields/CoachPermissions/Label')}
                                        help={st('Fields/CoachPermissions/Help')}
                                        infoHelp={coachPermissionsHelp}
                                        onChange={value => runSetCoachPermissions(value)}
                                        value={coachPermissions}
                                        allowMultiple
                                        data={disabledCoachPermissions()}
                                        data-testid="select-coach-permissions"
                                    />
                                )}
                                {hasModule(user, 'objectives') && (
                                    <Forms.Toggle
                                        label={st('Fields/ObjectivesPDF/Label')}
                                        help={st('Fields/ObjectivesPDF/Help')}
                                        onChange={value => setIncludeObjectivesInReviewPDF(value)}
                                        value={includeObjectivesInReviewPDF}
                                        data-testid="toggle-objectives-pdf"
                                    />
                                )}
                                <Forms.Toggle
                                    label={st('Fields/AllowReviewDelete/Label')}
                                    help={st('Fields/AllowReviewDelete/Help')}
                                    onChange={value => setAllowDeletionOfPastReviews(value)}
                                    value={allowDeletionOfPastReviews}
                                    data-testid="toggle-delete-review"
                                />
                                <Forms.Toggle
                                    label={st('Fields/GreyRatings/Label')}
                                    help={st('Fields/GreyRatings/Help')}
                                    onChange={value => setGreyRatingsForUserReviews(value)}
                                    value={greyRatingsForUserReviews}
                                    data-testid="toggle-grey-ratings"
                                />
                                <Forms.Toggle
                                    label={st('Fields/ExpandDescriptions/Label')}
                                    help={st('Fields/ExpandDescriptions/Help')}
                                    onChange={value => setExpandRatingDescriptions(value)}
                                    value={expandRatingDescriptions}
                                    data-testid="toggle-expand-descriptions"
                                />
                                <Forms.Toggle
                                    label={st('Fields/ShowRecommends/Label')}
                                    help={st('Fields/ShowRecommends/Help')}
                                    onChange={value => setShowRecommends(value)}
                                    value={showRecommends}
                                    data-testid="toggle-show-recommends"
                                />
                                <Forms.Toggle
                                    label={st('Fields/PositiveIndicators/Label')}
                                    help={st('Fields/PositiveIndicators/Help')}
                                    onChange={value => setUsePositiveIndicators(value)}
                                    value={usePositiveIndicators}
                                    data-testid="toggle-positive-indicators"
                                />
                                {usePositiveIndicators && (
                                    <>
                                        <Forms.Toggle
                                            label={st('Fields/PositiveIndicatorsPDF/Label')}
                                            help={st('Fields/PositiveIndicatorsPDF/Help')}
                                            onChange={value => setIncludePositiveIndicatorsInReviewPDF(value)}
                                            value={includePositiveIndicatorsInReviewPDF}
                                            data-testid="toggle-objectives-pdf"
                                        />
                                        {(user.support || usePerformanceDashboard) && (
                                            <Forms.Toggle
                                                label={st('Fields/UsePerformanceDashboard/Label')}
                                                help={st('Fields/UsePerformanceDashboard/Help')}
                                                onChange={value => setUsePerformanceDashboard(value)}
                                                value={usePerformanceDashboard}
                                                disabled={!user.support}
                                                data-testid="toggle-use-performance-dashboard"
                                            />
                                        )}
                                        <Forms.Toggle
                                            label={st('Fields/ShowNotes/Label')}
                                            help={st('Fields/ShowNotes/Help')}
                                            onChange={value => setShowNotesOnPerformanceDashboard(value)}
                                            value={showNotesOnPerformanceDashboard}
                                            data-testid="toggle-show-notes"
                                        />
                                    </>
                                )}
                                {user.reviewTypes?.some(x => x === 'UM') && !user.reviewViaSitdown && (
                                    <div className="form-group">
                                        <label>{st('Fields/ReviewWeighting/Label')}</label>
                                        <span className="form-control-help">{st('Fields/ReviewWeighting/Help')}</span>
                                        <div className="pointer slider-marked">
                                            <Slider
                                                data-testid="slider-review-percent"
                                                value={reviewWeighting}
                                                defaultValue={50}
                                                onChange={(e, x) => setReviewWeighting(x)}
                                                track="normal"
                                                marks
                                                step={5}
                                                min={0}
                                                max={100}
                                                valueLabelFormat={x => `${x}%`}
                                                valueLabelDisplay="on"
                                            />
                                        </div>
                                        <div className="flex">
                                            <span className="flex-30">100% User</span>
                                            <span className="flex-grow text-center">50-50</span>
                                            <span className="flex-30 text-right">100% Manager</span>
                                        </div>
                                    </div>
                                )}
                            </TitleBar>
                            <TitleBar title={st('Fields/DisplaySettings/Label')} data-testid="bar-display">
                                {typeof logo === 'object' || logo?.indexOf('http') < 0 ? (
                                    <Forms.Image
                                        id="logo"
                                        key="logo"
                                        label={st('Fields/Logo/Label')}
                                        help={st('Fields/Logo/Help')}
                                        value={logo}
                                        onChange={value => setLogo(value)}
                                        onMetaChange={value => setLogoMeta(value)}
                                        folder="logo"
                                        ratio={2.5}
                                        addImage={(f, s) => dispatch(addImage(f, s))}
                                        config={config}
                                        images={images}
                                        background="#000000"
                                        data-testid="image-logo"
                                    />
                                ) : (
                                    <>
                                        <Forms.Text
                                            id="logo"
                                            key="logo"
                                            readonly
                                            label={st('Fields/Logo/Label')}
                                            help={st('Fields/Logo/Help')}
                                            onChange={value => setLogo(value)}
                                            value={logo}
                                            data-testid="text-logo"
                                        />
                                        <button className="mtm10 mb10 btn btn-link" onClick={() => setLogo('')}>
                                            {st('Fields/Logo/Clear')}
                                        </button>
                                    </>
                                )}
                                <Forms.Toggle
                                    label={st('Fields/OnWhite/Label')}
                                    onChange={value => setOnWhite(value)}
                                    value={onWhite}
                                    data-testid="toggle-logo"
                                />
                                {!prism_UI && (
                                    <Forms.Toggle
                                        label={st('Fields/HideHomePage/Label')}
                                        help={st('Fields/HideHomePage/Help')}
                                        onChange={value => setHideHomePage(value)}
                                        value={hideHomePage}
                                        data-testid="toggle-hide-home"
                                    />
                                )}
                                {!prism_UI && !hideHomePage && (
                                    <Forms.Select
                                        allowMultiple
                                        data={homeWidgetData.sort((a, b) =>
                                            a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
                                        )}
                                        label={st('Fields/HomeWidgets/Label')}
                                        help={st('Fields/HomeWidgets/Help')}
                                        onChange={value => setHomeWidgets(value)}
                                        value={homeWidgets}
                                        data-testid="select-home-widgets"
                                    />
                                )}
                                <Forms.Toggle
                                    label={st('Fields/HideMeetings/Label')}
                                    help={st('Fields/HideMeetings/Help')}
                                    onChange={value => setHideMeetingsOnModel(value)}
                                    value={hideMeetingsOnModel}
                                    data-testid="toggle-hide-meetings"
                                />
                                <Forms.Text
                                    key="currencysymbol"
                                    id="currencysymbol"
                                    label={st('Fields/Currency/Label')}
                                    onChange={value => setCurrencySymbol(value)}
                                    value={currencySymbol}
                                    data-testid="text-currency"
                                />
                                {user.crmIntegration && hasModule(user, 'integrations') && (
                                    <Forms.Text
                                        label={st('Fields/MeetingDays/Label')}
                                        help={st('Fields/MeetingDays/Help')}
                                        onChange={value => setMeetingDays(value)}
                                        value={meetingDays}
                                        type="number"
                                        data-testid="text-meeting-days"
                                    />
                                )}
                                <Forms.Select
                                    key="ratingRange"
                                    id="ratingRange"
                                    label={st('Fields/RatingsRange/Label')}
                                    help={st('Fields/RatingsRange/Help')}
                                    mandatory
                                    data={[
                                        { id: 4, name: '1 - 4' },
                                        { id: 5, name: '1 - 5' },
                                    ]}
                                    onChange={value => setRatingRange(value)}
                                    value={ratingRange}
                                    data-testid="select-rating-range"
                                />
                                <Forms.Date
                                    id="fy_Start_Date"
                                    key="fy_Start_Date"
                                    label={st('Fields/FY/Label')}
                                    help={st('Fields/FY/Help')}
                                    onChange={value => setFy_Start_Date(value)}
                                    value={fy_Start_Date}
                                    data-testid="date-fy-start"
                                />
                                {hasModule(user, 'blogs') && (
                                    <Forms.Image
                                        id="defaultBlogImage"
                                        key="defaultBlogImage"
                                        label={st('Fields/BlogImage/Label')}
                                        help={st('Fields/BlogImage/Help')}
                                        value={defaultBlogImage}
                                        onChange={value => setDefaultBlogImage(value)}
                                        onMetaChange={value => setDefaultBlogImageMeta(value)}
                                        folder="defaultBlogImage"
                                        addImage={(f, s) => dispatch(addImage(f, s))}
                                        config={config}
                                        images={images}
                                        data-testid="image-blog"
                                    />
                                )}
                            </TitleBar>

                            <TitleBar title={st('Fields/ModulesAndNotifications/Label')} data-testid="bar-modules">
                                <div>
                                    <h3 className="section-header">{st('Fields/Notifications/Label')}</h3>
                                </div>
                                <Forms.Toggle
                                    label={st('Fields/SendReview/Label')}
                                    help={st('Fields/SendReview/Help')}
                                    onChange={value => setNotificationValue('review', value)}
                                    value={notifications?.some(m => m === 'review')}
                                    data-testid="toggle-send-review"
                                />
                                <Forms.Toggle
                                    label={st('Fields/SendReviewReminder/Label')}
                                    help={st('Fields/SendReviewReminder/Help')}
                                    onChange={value => setNotificationValue('review-reminder', value)}
                                    value={notifications?.some(m => m === 'review-reminder')}
                                    data-testid="toggle-send-review-reminder"
                                />
                                <Forms.Toggle
                                    label={st('Fields/SendReviewReminderWeekly/Label')}
                                    help={st('Fields/SendReviewReminderWeekly/Help')}
                                    onChange={value => setNotificationValue('review-reminder-weekly', value)}
                                    value={notifications?.some(m => m === 'review-reminder-weekly')}
                                    data-testid="toggle-send-review-reminder-weekly"
                                />
                                <Forms.Toggle
                                    label={st('Fields/SendReviewReminderMonthly/Label')}
                                    help={st('Fields/SendReviewReminderMonthly/Help')}
                                    onChange={value => setNotificationValue('review-reminder-monthly', value)}
                                    value={notifications?.some(m => m === 'review-reminder-monthly')}
                                    data-testid="toggle-send-review-reminder-monthly"
                                />
                                <Forms.Toggle
                                    label={st('Fields/SendDashboardUpate/Label')}
                                    help={st('Fields/SendDashboardUpate/Help')}
                                    onChange={value => setNotificationValue('dashboard', value)}
                                    value={notifications?.some(m => m === 'dashboard')}
                                    data-testid="toggle-send-dashboard"
                                />
                                <Forms.Toggle
                                    label={st('Fields/SendLinkedIn/Label')}
                                    help={st('Fields/SendLinkedIn/Help')}
                                    onChange={value => setNotificationValue('linkedin', value)}
                                    value={notifications?.some(m => m === 'linkedin')}
                                    data-testid="toggle-send-linkedin"
                                />
                                <div>
                                    <h3 className="section-header">{st('Fields/Modules/Label')}</h3>
                                </div>
                                <Forms.Toggle
                                    label={st('Fields/ShowIntegrations/Label')}
                                    help={st('Fields/ShowIntegrations/Help')}
                                    onChange={value => setModulesValue('integrations', value)}
                                    value={modules.some(m => m === 'integrations')}
                                    disabled={!canEditModules}
                                    data-testid="toggle-integrations"
                                />
                                <Forms.Toggle
                                    label={st('Fields/ShowSystemMetrics/Label')}
                                    help={st('Fields/ShowSystemMetrics/Help')}
                                    onChange={value => setModulesValue('e4', value)}
                                    value={modules.some(m => m === 'e4')}
                                    disabled={!canEditModules}
                                    data-testid="toggle-system"
                                />
                                <div className="flex">
                                    <div className="flex-50">
                                        <Forms.Toggle
                                            label={st('Fields/ShowBlogs/Label')}
                                            help={st('Fields/ShowBlogs/Help')}
                                            onChange={value => setModulesValue('blogs', value)}
                                            value={modules.some(m => m === 'blogs')}
                                            disabled={!canEditModules}
                                            data-testid="toggle-blogs"
                                        />
                                    </div>
                                    <div className="flex-50">
                                        <Forms.Toggle
                                            label={st('Fields/SendBlogs/Label')}
                                            help={st('Fields/SendBlogs/Help')}
                                            onChange={value => setNotificationValue('blogs', value)}
                                            value={notifications?.some(m => m === 'blogs')}
                                            disabled={!modules.some(m => m === 'blogs')}
                                            data-testid="toggle-blogs-emails"
                                        />
                                    </div>
                                </div>
                                <div className="flex">
                                    <div className="flex-50">
                                        <Forms.Toggle
                                            label={st(`Fields/ShowKnowledgeBank/Label${prismSuffix}`)}
                                            help={st(`Fields/ShowKnowledgeBank/Help${prismSuffix}`)}
                                            onChange={value => setModulesValue('knowledge', value)}
                                            value={modules.some(m => m === 'knowledge')}
                                            disabled={!canEditModules}
                                            data-testid="toggle-knowledge"
                                        />
                                    </div>
                                    <div className="flex-50">
                                        <Forms.Toggle
                                            label={st(`Fields/SendKnowledgeBank/Label${prismSuffix}`)}
                                            help={st(`Fields/SendKnowledgeBank/Help${prismSuffix}`)}
                                            onChange={value => setNotificationValue('knowledge', value)}
                                            value={notifications?.some(m => m === 'knowledge')}
                                            disabled={!modules.some(m => m === 'knowledge')}
                                            data-testid="toggle-knowledge-emails"
                                        />
                                    </div>
                                </div>
                                <div className="flex">
                                    <div className="flex-50">
                                        <Forms.Toggle
                                            label={st('Fields/ShowObjectives/Label')}
                                            help={st('Fields/ShowObjectives/Help')}
                                            onChange={value => setModulesValue('objectives', value)}
                                            value={modules.some(m => m === 'objectives')}
                                            disabled={!canEditModules}
                                            data-testid="toggle-objectives"
                                        />
                                    </div>
                                    <div className="flex-50">
                                        <Forms.Toggle
                                            label={st('Fields/SendObjectives/Label')}
                                            help={st('Fields/SendObjectives/Help')}
                                            onChange={value => setNotificationValue('objectives', value)}
                                            value={notifications?.some(m => m === 'objectives')}
                                            disabled={!modules.some(m => m === 'objectives')}
                                            data-testid="toggle-objectives-emails"
                                        />
                                    </div>
                                </div>
                                <div className="flex">
                                    <div className="flex-50">
                                        <Forms.Toggle
                                            label={st('Fields/ShowPathways/Label')}
                                            help={st('Fields/ShowPathways/Help')}
                                            onChange={value => setModulesValue('pathways', value)}
                                            value={modules.some(m => m === 'pathways')}
                                            disabled={!canEditModules}
                                            data-testid="toggle-pathways"
                                        />
                                    </div>
                                    <div className="flex-50">
                                        <Forms.Toggle
                                            label={st('Fields/SendPathways/Label')}
                                            help={st('Fields/SendPathways/Help')}
                                            onChange={value => setNotificationValue('pathways', value)}
                                            value={notifications?.some(m => m === 'pathways')}
                                            disabled={!modules.some(m => m === 'pathways')}
                                            data-testid="toggle-pathways-emails"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <h3 className="section-header">{st('Fields/AllNotifications/Label')}</h3>
                                </div>
                                <Forms.Toggle
                                    label={st('Fields/SendAll/Label')}
                                    help={st('Fields/SendAll/Help')}
                                    onChange={value => {
                                        value ? setNotificationValue('all', true) : setShowEmailWarning(true);
                                    }}
                                    value={notifications.some(m => m === 'all')}
                                    disabled={!canEditModules}
                                    data-testid="toggle-all-emails"
                                />
                            </TitleBar>
                            <TitleBar title={st('Fields/Onboarding/Label')} data-testid="bar-onboarding">
                                <Forms.Textarea
                                    tags={tags}
                                    handles={handles}
                                    label={st('Fields/IntroEmail/Label')}
                                    help={st('Fields/IntroEmail/Help')}
                                    rows="10"
                                    onChange={value => setIntroEmail(value)}
                                    value={introEmail}
                                    data-testid="text-intro-email"
                                />
                            </TitleBar>
                        </div>
                        <div className="col-md-12 pull-right mt10">
                            <SaveButton
                                data-testid="button-save"
                                saving={saving}
                                setSaving={setSaving}
                                className="btn btn-success"
                                onClick={save}
                            >
                                {st('Buttons/Save')}
                            </SaveButton>
                        </div>
                    </div>
                )}
            </section>
            <Modal title={st('EmailModal/Title')} show={showEmailWarning} close={() => checkDisable(false)} footer={null}>
                <p>{st('EmailModal/Body')}</p>
                <button className="btn btn-primary" onClick={() => checkDisable(true)}>
                    {st('EmailModal/Button')}
                </button>
            </Modal>
        </div>
    );
};

export default AdminSettings;
